// ARreport.js
import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  CircularProgress,
  Typography,
  Paper,
  Tabs,
  Tab,
  AppBar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Modal,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import Cookies from "universal-cookie";
import { useSiteDataApi } from "../utilities/SiteDataProvider";
import { tableCellClasses } from "@mui/material/TableCell";

const cookies = new Cookies();

// Read the username from the cookie.
const username = cookies.get("username");

console.log("Username from cookie:", username);

// For admin purposes, we consider these values as admin:
const isAdmin =
  username === "TrueHearing Health" ||
  username === "TrueHearing%20Health" ||
  username === "TaylorField";
// Windsor-specific admin: show commission info only for Windsor tab.
console.log("Is admin:", isAdmin);
const isWindsorUser = username === "SaraTaba";

// Helper: consider 90 days (approx. 3 months) in milliseconds
const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000;
const isOldDate = (dateStr) =>
  Date.now() - new Date(dateStr).getTime() > THREE_MONTHS_MS;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

// Simple grouping helper (returns a Map)
function groupBy(list, keyGetter) {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

/* SUMMARY TABLE COMPONENTS */

// For tabs other than Total (simple summary with only insurer and total)
function SummaryTableSimple({ summaryData }) {
  const overall = summaryData.reduce((acc, item) => acc + item.totalOwing, 0);
  return (
    <Box mb={2}>
      <Typography variant="subtitle1">Summary Owing</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Insurer</StyledTableCell>
              <StyledTableCell align="right">Total Owing</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {summaryData.map((item, idx) => (
              <StyledTableRow key={idx}>
                <StyledTableCell className={item.hasOld ? "fail" : ""}>
                  {item.insurerName}
                </StyledTableCell>
                <StyledTableCell align="right">
                  {`$${item.totalOwing.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                </StyledTableCell>
              </StyledTableRow>
            ))}
            <StyledTableRow>
              <StyledTableCell>
                <strong>Overall</strong>
              </StyledTableCell>
              <StyledTableCell align="right">
                <strong>
                  {`$${overall.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                </strong>
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

SummaryTableSimple.propTypes = {
  summaryData: PropTypes.arrayOf(
    PropTypes.shape({
      insurerName: PropTypes.string.isRequired,
      totalOwing: PropTypes.number.isRequired,
      hasOld: PropTypes.bool,
    })
  ).isRequired,
};

// For the Total tab: summary with a breakdown per location (one column per location)
function SummaryTableWithBreakdown({ summaryData, breakdownLocations }) {
  const overallBreakdown = {};
  breakdownLocations.forEach((loc) => {
    overallBreakdown[loc] = summaryData.reduce(
      (acc, item) => acc + (item.breakdown[loc] || 0),
      0
    );
  });
  const overallTotal = summaryData.reduce(
    (acc, item) => acc + item.totalOwing,
    0
  );
  return (
    <Box mb={2}>
      <Typography variant="subtitle1">Summary Owing</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Insurer</StyledTableCell>
              {breakdownLocations.map((loc, idx) => (
                <StyledTableCell key={idx} align="right">
                  {loc}
                </StyledTableCell>
              ))}
              <StyledTableCell align="right">Total</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {summaryData.map((item, idx) => (
              <StyledTableRow key={idx}>
                <StyledTableCell className={item.hasOld ? "fail" : ""}>
                  {item.insurerName}
                </StyledTableCell>
                {breakdownLocations.map((loc, i) => (
                  <StyledTableCell key={i} align="right">
                    {`$${(item.breakdown[loc] || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                  </StyledTableCell>
                ))}
                <StyledTableCell align="right">
                  {`$${item.totalOwing.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                </StyledTableCell>
              </StyledTableRow>
            ))}
            <StyledTableRow>
              <StyledTableCell>
                <strong>Overall</strong>
              </StyledTableCell>
              {breakdownLocations.map((loc, i) => (
                <StyledTableCell key={i} align="right">
                  <strong>
                    {`$${overallBreakdown[loc].toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                  </strong>
                </StyledTableCell>
              ))}
              <StyledTableCell align="right">
                <strong>
                  {`$${overallTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                </strong>
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

SummaryTableWithBreakdown.propTypes = {
  summaryData: PropTypes.arrayOf(
    PropTypes.shape({
      insurerName: PropTypes.string.isRequired,
      totalOwing: PropTypes.number.isRequired,
      hasOld: PropTypes.bool,
      breakdown: PropTypes.object.isRequired,
    })
  ).isRequired,
  breakdownLocations: PropTypes.array.isRequired,
};

/* INSURER TABLE / ACCORDION WITH UNIQUE ID, TOOLTIP, & IGNORE */

// InsurerTable renders the invoice rows. Each row's unique ID is built from
// earliest_invoice_date, client_id, total_covered, and total_remaining.
// When the Control key is held, that unique ID is shown in a tooltip.
// If an admin (based on the cookie username) ctrl+clicks a row, a modal is shown to confirm ignoring the line.
function InsurerTable({ insurerName, data, includeLocation, onIgnore }) {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [ignoreCandidate, setIgnoreCandidate] = useState(null);
  const handleConfirm = () => {
    onIgnore(ignoreCandidate);
    setShowIgnoreModal(false);
  };
  // Listen for Control key press/release
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Control") setCtrlPressed(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === "Control") setCtrlPressed(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const sortedData = React.useMemo(() => {
    let sorted = [...data];
    if (sortColumn) {
      sorted.sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];
        if (sortColumn === "earliest_invoice_date") {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        } else if (
          sortColumn === "total_covered" ||
          sortColumn === "total_remaining"
        ) {
          aVal = parseFloat(aVal);
          bVal = parseFloat(bVal);
        } else if (sortColumn === "name") {
          aVal = a.surname + a.given_name;
          bVal = b.surname + b.given_name;
        } else if (sortColumn === "location") {
          aVal = a.location;
          bVal = b.location;
        }
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [data, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // When an admin ctrl+clicks a row, prompt to ignore it.
  const handleRowClick = (e, uniqueId) => {
    // Use cookie username instead of logged in email.
    if (
      (username === "TrueHearing Health" ||
        username === "TrueHearing%20Health" ||
        username === "TaylorField") &&
      e.ctrlKey
    ) {
      setIgnoreCandidate(uniqueId);
      setShowIgnoreModal(true);
    }
  };
  // Confirm ignore by calling backend
  const confirmIgnore = () => {
    onIgnore(ignoreCandidate);
    setShowIgnoreModal(false);
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650 }}
          size="small"
          aria-label={`${insurerName} table`}
        >
          <TableHead>
            <StyledTableRow>
              {includeLocation && (
                <StyledTableCell onClick={() => handleSort("location")}>
                  Location
                </StyledTableCell>
              )}
              <StyledTableCell onClick={() => handleSort("name")}>
                Name
              </StyledTableCell>
              <StyledTableCell
                onClick={() => handleSort("earliest_invoice_date")}
              >
                Earliest Invoice Date
              </StyledTableCell>
              <StyledTableCell onClick={() => handleSort("total_covered")}>
                Total Covered
              </StyledTableCell>
              <StyledTableCell onClick={() => handleSort("total_remaining")}>
                Total Remaining
              </StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, idx) => {
              // Compute unique ID including amounts.
              const uniqueId = `${row.earliest_invoice_date}-${row.client_id}-${row.total_covered}-${row.total_remaining}`;
              return (
                <Tooltip key={idx} title={ctrlPressed ? uniqueId : ""} arrow>
                  <StyledTableRow onClick={(e) => handleRowClick(e, uniqueId)}>
                    {includeLocation && <TableCell>{row.location}</TableCell>}
                    <TableCell>{`${row.surname}, ${row.given_name}`}</TableCell>
                    <TableCell
                      className={
                        isOldDate(row.earliest_invoice_date) ? "fail" : ""
                      }
                    >
                      {new Date(row.earliest_invoice_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {parseFloat(row.total_covered).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell>
                      {parseFloat(row.total_remaining).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )}
                    </TableCell>
                  </StyledTableRow>
                </Tooltip>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Modal for ignore confirmation */}
      <Modal open={showIgnoreModal} onClose={() => setShowIgnoreModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 1,
            boxShadow: 24,
          }}
        >
          <Typography variant="h6" component="h2">
            Ignore Invoice
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Do you want to ignore the invoice with ID:{" "}
            <strong>{ignoreCandidate}</strong>?
          </Typography>
          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button variant="contained" color="primary" onClick={confirmIgnore}>
              Confirm
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowIgnoreModal(false)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

InsurerTable.propTypes = {
  insurerName: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  includeLocation: PropTypes.bool,
  onIgnore: PropTypes.func.isRequired,
};

InsurerTable.defaultProps = {
  includeLocation: false,
};

function InsurerAccordion({ insurerName, data, includeLocation, onIgnore }) {
  const totalOwing = data.reduce(
    (acc, row) => acc + parseFloat(row.total_remaining),
    0
  );
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">
          {insurerName} ($
          {totalOwing.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          )
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <InsurerTable
          insurerName={insurerName}
          data={data}
          onIgnore={onIgnore}
          includeLocation={includeLocation}
        />
      </AccordionDetails>
    </Accordion>
  );
}

InsurerAccordion.propTypes = {
  insurerName: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  includeLocation: PropTypes.bool,
  onIgnore: PropTypes.func.isRequired,
};

InsurerAccordion.defaultProps = {
  includeLocation: false,
};

// TabPanel helper component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
    key: index,
  };
}

/* MAIN ARREPORT COMPONENT */
const ARreport = () => {
  const [activeTab, setActiveTab] = useState(
    parseInt(cookies.get("activeClinic")) || 0
  );
  const [ARData, setARData] = useState({}); // grouped by location
  const [totalData, setTotalData] = useState({}); // grouped by insurer (all data)
  const [ignoredIDs, setIgnoredIDs] = useState([]);
  const [siteDataContext] = useSiteDataApi();
  // Filter state: "all" or "overdue" (">3 Months Only")
  const [filter, setFilter] = useState("all");
  // Global control key for ARreport
  const [ctrlPressed, setCtrlPressed] = useState(false);

  // now inside ARreport, so siteDataContext & setIgnoredIDs are in scope:
  const handleIgnore = async (ignoreID) => {
    await fetch("/api/arIgnores", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": siteDataContext.thhcsfrtoken,
      },
      body: JSON.stringify({ ignoreID }),
    });
    setIgnoredIDs((ids) => [...ids, ignoreID]);
  };

  // Fetch ignored IDs from backend
  useEffect(() => {
    async function fetchIgnoredIDs() {
      try {
        const res = await fetch("/api/arIgnores", {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": siteDataContext.thhcsfrtoken,
          },
        });
        const data = await res.json();
        console.log("Ignored IDs:", data);
        // Expect an array of objects with an ignoreID property.
        setIgnoredIDs(data.map((row) => row.ignoreID));
      } catch (error) {
        console.error("Error fetching ignored IDs:", error);
      }
    }
    fetchIgnoredIDs();
  }, []);

  // Global Control key listener for ARreport
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Control") setCtrlPressed(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === "Control") setCtrlPressed(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Fetch AR data, filter out ignored rows, and group them.
  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch("/api/insurer-ar", {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": siteDataContext.thhcsfrtoken,
          },
        });
        const { returnData } = await res.json();
        let raw = returnData[0];
        // filter out ignores:
        raw = raw.filter((row) => {
          const id = `${row.earliest_invoice_date}-${row.client_id}-${row.total_covered}-${row.total_remaining}`;
          return !ignoredIDs.includes(id);
        });
        // Group by location then by insurer.
        const locationMap = groupBy(raw, (item) => item.location);
        let finalData = {};
        locationMap.forEach((items, location) => {
          const insurerMap = groupBy(items, (item) => item.insurance_name);
          const insurerObj = Object.fromEntries(insurerMap);
          const sortedInsurerObj = {};
          Object.keys(insurerObj)
            .sort()
            .forEach((insurer) => {
              sortedInsurerObj[insurer] = insurerObj[insurer];
            });
          finalData[location] = sortedInsurerObj;
        });
        setARData(finalData);

        // Group all data by insurer for the Total tab.
        const totalMap = groupBy(raw, (item) => item.insurance_name);
        const totalObj = Object.fromEntries(totalMap);
        const sortedTotalObj = {};
        Object.keys(totalObj)
          .sort()
          .forEach((insurer) => {
            sortedTotalObj[insurer] = totalObj[insurer];
          });
        setTotalData(sortedTotalObj);
      } catch (error) {
        console.log("error", error);
      }
    }
    fetchData();
  }, [siteDataContext.thhcsfrtoken, ignoredIDs]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    cookies.set("activeClinic", newValue, {
      path: "/",
      secure: true,
      sameSite: "none",
    });
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const getMonthsOverdue = (dateStr) => {
    const now = Date.now();
    const invoiceTime = new Date(dateStr).getTime();
    const msInMonth = 30 * 24 * 60 * 60 * 1000; // approximate
    return (now - invoiceTime) / msInMonth;
  };

  // New commission functions using average overdue age multiplier.
  const computeClinicCommission = (location) => {
    let totalOverdue = 0;
    let sumAge = 0;
    let count = 0;
    if (!ARData[location]) return { weightedOverdue: 0, bonus: 0 };
    Object.values(ARData[location]).forEach((insurerArray) => {
      insurerArray.forEach((row) => {
        if (isOldDate(row.earliest_invoice_date)) {
          const amt = parseFloat(row.total_remaining);
          totalOverdue += amt;
          const monthsOverdue = getMonthsOverdue(row.earliest_invoice_date);
          sumAge += monthsOverdue;
          count++;
        }
      });
    });
    const avgAge = count > 0 ? sumAge / count : 3;
    const multiplier = avgAge <= 3 ? 1 : 1 + (avgAge - 3) / 30;
    const weightedOverdue = totalOverdue * multiplier;
    let bonus = 0;
    if (weightedOverdue <= 6000) bonus = 167;
    else if (weightedOverdue >= 35000) bonus = 0;
    else bonus = 167 * (1 - (weightedOverdue - 6000) / (35000 - 6000));
    return { weightedOverdue, bonus };
  };

  const computeOverallCommission = () => {
    let totalOverdue = 0;
    let sumAge = 0;
    let count = 0;
    Object.values(ARData).forEach((locationObj) => {
      Object.values(locationObj).forEach((insurerArray) => {
        insurerArray.forEach((row) => {
          if (isOldDate(row.earliest_invoice_date)) {
            const amt = parseFloat(row.total_remaining);
            totalOverdue += amt;
            const monthsOverdue = getMonthsOverdue(row.earliest_invoice_date);
            sumAge += monthsOverdue;
            count++;
          }
        });
      });
    });
    const avgAge = count > 0 ? sumAge / count : 3;
    const multiplier = avgAge <= 3 ? 1 : 1 + (avgAge - 3) / 30;
    const weightedOverdue = totalOverdue * multiplier;
    let bonus = 0;
    if (weightedOverdue <= 6000) bonus = 167;
    else if (weightedOverdue >= 35000) bonus = 0;
    else bonus = 167 * (1 - (weightedOverdue - 6000) / (35000 - 6000));
    return { weightedOverdue, bonus };
  };

  // Decide whether to show commission info.
  // Only show if Control is pressed and the user is an admin.
  // If the username is "SaraTaba", then show info only for the Windsor tab.
  const showCommissionInfo = (tabLabel) => {
    if (!(isAdmin || username === "SaraTaba")) return false;
    if (username === "SaraTaba" && tabLabel !== "Windsor") return false;
    return ctrlPressed;
  };

  if (Object.keys(ARData).length === 0 && Object.keys(totalData).length === 0) {
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: "100vh" }}
      >
        <Grid item xs={3}>
          <CircularProgress style={{ display: "block", margin: "0 auto" }} />
          <br />
          <Typography style={{ textAlign: "center" }}>
            Loading insurer data...
          </Typography>
        </Grid>
      </Grid>
    );
  }

  // Prepare tab labels: one per location plus a "Total" tab.
  const locationKeys = Object.keys(ARData);
  const tabLabels = [...locationKeys, "Total"];

  return (
    <Box m={3} className="insurerDataPage">
      {/* Toggle button to select filter mode */}
      <ToggleButtonGroup
        value={filter}
        exclusive
        onChange={handleFilterChange}
        aria-label="Filter"
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "center",
          "& .MuiToggleButton-root.Mui-selected": {
            backgroundColor: "#00b4aa",
            color: "#FFFFFF",
          },
        }}
      >
        <ToggleButton value="all" aria-label="show all">
          Show All
        </ToggleButton>
        <ToggleButton value="overdue" aria-label="overdue only">
          &gt;3 Months Only
        </ToggleButton>
      </ToggleButtonGroup>
      <AppBar position="static">
        <Tabs
          value={activeTab}
          variant="fullWidth"
          onChange={handleTabChange}
          indicatorColor="secondary"
          textColor="inherit"
          aria-label="fullwidth tabs"
        >
          {tabLabels.map((label, idx) => (
            <Tab label={label} {...a11yProps(idx)} key={idx} />
          ))}
        </Tabs>
      </AppBar>
      {tabLabels.map((label, idx) => (
        <TabPanel value={activeTab} index={idx} key={idx}>
          {/* Show commission info if admin and control is pressed.
              For "SaraTaba", only show for Windsor. */}
          {showCommissionInfo(label) && (
            <Box mb={2} p={2} sx={{ bgcolor: "#eee", borderRadius: 1 }}>
              {label === "Total"
                ? (() => {
                    const { weightedOverdue, bonus } =
                      computeOverallCommission();
                    return (
                      <Typography variant="body2">
                        Overall Commission Info:
                        <br />
                        Weighted Overdue AR: $
                        {weightedOverdue.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        <br />
                        Commission Bonus: $
                        {bonus.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        <br />
                        (Weighted Overdue AR is calculated by first summing the
                        overdue invoice amounts. Then, the average overdue age
                        (in months) is computed and used to form a multiplier: 1
                        + ((average overdue months - 3) / 30). For example, if
                        the average overdue age is 3 months, the multiplier is
                        1; if it's 6 months, the multiplier is 1.15; if 12
                        months, 1.45. This weighted overdue AR is then compared
                        against thresholds of $6,000 (full bonus) and $35,000
                        (no bonus), with a linear decline in between.)
                      </Typography>
                    );
                  })()
                : (() => {
                    const { weightedOverdue, bonus } =
                      computeClinicCommission(label);
                    return (
                      <>
                        <Typography variant="body2">
                          Commission Info for {label}:
                        </Typography>
                        <Typography variant="body2">
                          Weighted Overdue AR: $
                          {weightedOverdue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                        <Typography variant="body2">
                          Commission Bonus: $
                          {bonus.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                        <Typography variant="body2">
                          (Weighted Overdue AR is calculated by summing overdue
                          invoice amounts, then multiplying by a factor based on
                          the average overdue age in months. The factor is 1 if
                          the average is 3 months, and increases as (1 +
                          ((average - 3) / 20)) for older invoices. A weighted
                          overdue AR of $6,000 or less yields a full bonus,
                          while $35,000 or more yields no bonus, with a linear
                          decline between these thresholds.)
                        </Typography>
                      </>
                    );
                  })()}
            </Box>
          )}

          {label === "Total" ? (
            // Total tab: render summary with breakdown and insurer accordions.
            <>
              {Object.keys(totalData).length > 0 &&
                (() => {
                  const summaryDataTotal = Object.keys(totalData)
                    .map((insurer) => {
                      const rows =
                        filter === "overdue"
                          ? totalData[insurer].filter((row) =>
                              isOldDate(row.earliest_invoice_date)
                            )
                          : totalData[insurer];
                      const totalOwing = rows.reduce(
                        (acc, row) => acc + parseFloat(row.total_remaining),
                        0
                      );
                      if (rows.length === 0 || totalOwing === 0) return null;
                      const hasOld = rows.some((row) =>
                        isOldDate(row.earliest_invoice_date)
                      );
                      const breakdown = {};
                      rows.forEach((row) => {
                        const loc = row.location;
                        breakdown[loc] =
                          (breakdown[loc] || 0) +
                          parseFloat(row.total_remaining);
                      });
                      return {
                        insurerName: insurer,
                        totalOwing,
                        hasOld,
                        breakdown,
                      };
                    })
                    .filter((item) => item !== null && item.totalOwing > 0);
                  const breakdownLocations = Array.from(
                    new Set(
                      summaryDataTotal.flatMap((item) =>
                        Object.keys(item.breakdown)
                      )
                    )
                  ).sort();
                  return (
                    <SummaryTableWithBreakdown
                      breakdownLocations={breakdownLocations}
                      summaryData={summaryDataTotal}
                    />
                  );
                })()}
              {Object.keys(totalData).map((insurer, index) => {
                const rows =
                  filter === "overdue"
                    ? totalData[insurer].filter((row) =>
                        isOldDate(row.earliest_invoice_date)
                      )
                    : totalData[insurer];
                const totalOwing = rows.reduce(
                  (acc, row) => acc + parseFloat(row.total_remaining),
                  0
                );
                if (rows.length === 0 || totalOwing === 0) return null;
                return (
                  <Box key={index} mb={4}>
                    <InsurerAccordion
                      insurerName={insurer}
                      data={rows}
                      includeLocation={true}
                      onIgnore={handleIgnore}
                    />
                  </Box>
                );
              })}
            </>
          ) : (
            // Location-specific tab: render summary and insurer accordions.
            <>
              {ARData[label] && (
                <SummaryTableSimple
                  summaryData={Object.keys(ARData[label])
                    .map((insurer) => {
                      const rows =
                        filter === "overdue"
                          ? ARData[label][insurer].filter((row) =>
                              isOldDate(row.earliest_invoice_date)
                            )
                          : ARData[label][insurer];
                      const totalOwing = rows.reduce(
                        (acc, row) => acc + parseFloat(row.total_remaining),
                        0
                      );
                      const hasOld = rows.some((row) =>
                        isOldDate(row.earliest_invoice_date)
                      );
                      return { insurerName: insurer, totalOwing, hasOld };
                    })
                    .filter((item) => item.totalOwing > 0)}
                />
              )}
              {ARData[label] &&
                Object.keys(ARData[label]).map((insurer, index) => {
                  const rows =
                    filter === "overdue"
                      ? ARData[label][insurer].filter((row) =>
                          isOldDate(row.earliest_invoice_date)
                        )
                      : ARData[label][insurer];
                  const totalOwing = rows.reduce(
                    (acc, row) => acc + parseFloat(row.total_remaining),
                    0
                  );
                  if (rows.length === 0 || totalOwing === 0) return null;
                  return (
                    <Box key={index} mb={4}>
                      <InsurerAccordion
                        insurerName={insurer}
                        data={rows}
                        includeLocation={false}
                        onIgnore={handleIgnore}
                      />
                    </Box>
                  );
                })}
            </>
          )}
        </TabPanel>
      ))}
    </Box>
  );
};

export default ARreport;
